// UpgradeCreditPage — /credit/:token. Thread mct_b219329b-b14; Michael's yes
// 4 Nov 2026 (mca_b8268f7bfe9e). SOP: sop-ee-upgrade-credits.
//
// A person's own upgrade credit: what they paid for one purchase (first use: an
// NA Summit pass) comes off their first year of a membership. The Engine works
// out the figures; this page shows them and sends the person on to OUR checkout
// page with their own code. Opening it changes nothing (email scanners open
// links). Never a Spiffy link.

import { useEffect, useState, type CSSProperties } from 'react';
import { useParams } from 'react-router-dom';
import TakerShell from './components/TakerShell';

const API = import.meta.env.VITE_API_BASE_URL as string;

interface Credit {
  firstName: string | null;
  label: string;
  currency: string;
  creditCents: number;
  priceCents: number;
  payCents: number;
  checkoutSlug: string;
  code: string | null;
  email: string;
  expiresAt: string;
  status: 'ready' | 'used' | 'ended';
}

const h1: CSSProperties = { fontFamily: "'Fraunces', serif", fontSize: 34, fontWeight: 900, color: '#0A0A2E', lineHeight: 1.1, margin: '0 0 12px' };
const lead: CSSProperties = { fontSize: 17, color: '#44445e', lineHeight: 1.55, margin: '0 0 20px' };
const card: CSSProperties = { background: '#fff', borderRadius: 16, padding: '20px 20px 22px', boxShadow: '0 4px 24px rgba(10,10,46,0.06)', marginBottom: 18 };
const button: CSSProperties = { display: 'block', textAlign: 'center', textDecoration: 'none', background: '#F05A28', color: '#fff', borderRadius: 10, padding: '14px 22px', fontSize: 17, fontWeight: 700 };
const line: CSSProperties = { display: 'flex', justifyContent: 'space-between', fontSize: 16, color: '#44445e', padding: '6px 0' };

function money(cents: number, currency: string): string {
  return `$${(cents / 100).toFixed(cents % 100 ? 2 : 0)} ${currency}`;
}

function endsOn(iso: string): string {
  const d = new Date(iso);
  return Number.isFinite(d.getTime())
    ? d.toLocaleDateString('en-US', { weekday: 'long', day: 'numeric', month: 'long', timeZone: 'America/Toronto' })
    : 'soon';
}

export default function UpgradeCreditPage() {
  const { token = '' } = useParams();
  const [data, setData] = useState<Credit | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    fetch(`${API}/api/upgrade-credit/${encodeURIComponent(token)}`)
      .then(async (r) => { if (!r.ok) throw new Error(String(r.status)); return r.json() as Promise<Credit> })
      .then(setData)
      .catch(() => setFailed(true));
  }, [token]);

  const checkoutHref = data
    ? `/checkout/${encodeURIComponent(data.checkoutSlug)}?${new URLSearchParams({
        cur: data.currency, email: data.email, ...(data.code ? { code: data.code } : {}),
      }).toString()}`
    : '#';

  return (
    <TakerShell footerLabel="The Successful Bookkeeper">
      <div style={{ maxWidth: 640, margin: '0 auto', padding: '40px 16px 80px' }}>
        {failed && (
          <>
            <h1 style={h1}>This link isn't working</h1>
            <p style={lead}>Reply to our email and we'll sort it out for you.</p>
          </>
        )}
        {!failed && !data && <p style={lead}>Loading…</p>}
        {data?.status === 'used' && (
          <>
            <h1 style={h1}>You're all set</h1>
            <p style={lead}>Your credit is already on your Member Plus plan. Nothing more to do here.</p>
          </>
        )}
        {data?.status === 'ended' && (
          <>
            <h1 style={h1}>This offer has ended</h1>
            <p style={lead}>Your credit was open until {endsOn(data.expiresAt)}. Reply to our email if you have any questions.</p>
          </>
        )}
        {data?.status === 'ready' && (
          <>
            <h1 style={h1}>{data.firstName ? `${data.firstName}, your` : 'Your'} Summit pass counts toward Member Plus</h1>
            <p style={lead}>
              Everything you paid for your pass comes off your first year of Member Plus, so you only pay the difference.
            </p>
            <div style={card}>
              <div style={line}><span>Member Plus, first year</span><span>{money(data.priceCents, data.currency)}</span></div>
              <div style={line}><span>Your Summit credit</span><span>− {money(data.creditCents, data.currency)}</span></div>
              <div style={{ ...line, borderTop: '1px solid #e6e4ef', marginTop: 6, paddingTop: 12, fontWeight: 700, color: '#0A0A2E' }}>
                <span>You pay</span><span>{money(data.payCents, data.currency)}</span>
              </div>
              <p style={{ fontSize: 14, color: '#6b6b80', margin: '10px 0 18px' }}>
                Plus tax where it applies. Then {money(data.priceCents, data.currency)} a year. Open until {endsOn(data.expiresAt)}.
              </p>
              <a style={button} href={checkoutHref}>Continue to checkout</a>
            </div>
            <p style={{ fontSize: 14, color: '#6b6b80' }}>
              {data.code
                ? 'Your credit is already applied at checkout, just for you.'
                : 'We put your credit on your plan the moment you check out, before your first payment.'}
            </p>
          </>
        )}
      </div>
    </TakerShell>
  );
}
